import { useState } from 'react';
import type { ReconstructionSummary } from '../types';

/** Base URL of the main app that serves the public /reconstruct/<token> page. */
const APP_URL = (import.meta.env.VITE_APP_URL || window.location.origin).replace(/\/+$/, '');

export function reconstructionShareUrl(shareToken: string): string {
  return `${APP_URL}/reconstruct/${shareToken}`;
}

interface ReconstructionShareCardProps {
  reconstruction: ReconstructionSummary;
}

/**
 * Card shown after a reconstruction is created (and for the creator's existing
 * reconstructions). Surfaces the public share link with copy + email actions.
 * Filling the reconstruction requires no login — only a name.
 */
export function ReconstructionShareCard({ reconstruction }: ReconstructionShareCardProps) {
  const [copied, setCopied] = useState(false);
  const shareUrl = reconstructionShareUrl(reconstruction.share_token);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  const mailtoHref = (() => {
    const subject = `שחזור מבחן: ${reconstruction.title}`;
    const body =
      `הוזמנת לשחזר מבחן יחד.\n\n` +
      `${reconstruction.title}` +
      (reconstruction.exam_date ? ` (${reconstruction.exam_date})` : '') +
      `\n\nמלאו את השחזור כאן (ללא צורך בהתחברות):\n${shareUrl}`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  })();

  return (
    <article
      data-testid="reconstruction-card"
      data-share-token={reconstruction.share_token}
      className="card flex flex-col gap-4 p-6"
    >
      <div>
        <h3 className="text-lg font-bold text-slate-950" dir="auto">
          {reconstruction.title}
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-600">
          {reconstruction.exam_date && (
            <span data-testid="reconstruction-exam-date">📅 {reconstruction.exam_date}</span>
          )}
          <span>{reconstruction.question_count} שאלות</span>
          <span>{reconstruction.participant_count} משתתפים</span>
        </div>
      </div>

      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
        קישור ציבורי לשיתוף
      </label>
      <input
        data-testid="reconstruction-share-url"
        readOnly
        value={shareUrl}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
        dir="ltr"
      />

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          data-testid="reconstruction-copy-link"
          onClick={copyLink}
          className="btn-primary"
        >
          {copied ? 'הועתק!' : 'העתק קישור'}
        </button>
        <a
          data-testid="reconstruction-email-link"
          href={mailtoHref}
          className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
        >
          שליחה במייל
        </a>
        <a
          data-testid="reconstruction-open-link"
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
        >
          פתיחת השחזור
        </a>
      </div>
    </article>
  );
}
